import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import api from '../utils/api';

// Components
import PageHeader from '../components/ui/PageHeader';
import ProjectCard from '../components/projects/ProjectCard';
import FilterButtons from '../components/projects/FilterButtons';
import SearchBar from '../components/ui/SearchBar';
import PageTransition from '../components/ui/PageTransition';
import portfolioProject from '../assets/images/portfolio-project.svg';
import businessConsulting from '../assets/images/business-consulting.svg';
import sortingVisualizer from '../assets/images/sorting-visualizer.svg';
import dsms from '../assets/images/dsms.svg';

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Animation variants
  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  // Fetch projects
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const response = await api.get('/projects');
        const data = response.data;

        setProjects(data);
        setFilteredProjects(data);

        // Extract unique categories
        const uniqueCategories = ['All', ...new Set(data.map(project => project.category))];
        setCategories(uniqueCategories);

        setLoading(false);
      } catch (error) {
        setError('Failed to fetch projects. Please try again later.');
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  // Filter projects by category and search term
  useEffect(() => {
    let result = projects;

    // Filter by category
    if (activeCategory !== 'All') {
      result = result.filter(project => project.category === activeCategory);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(project =>
        project.title.toLowerCase().includes(term) ||
        project.description.toLowerCase().includes(term) ||
        project.tags.some(tag => tag.toLowerCase().includes(term))
      );
    }

    setFilteredProjects(result);
  }, [activeCategory, searchTerm, projects]);

  // Handle category change
  const handleCategoryChange = (category) => {
    setActiveCategory(category);
  };

  // Handle search
  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  return (
    <PageTransition>
      <ProjectsContainer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        <PageHeader
          title="My Projects"
          subtitle="Explore my recent work and creative solutions"
        />

        <FilterContainer>
          <SearchBar onSearch={handleSearch} />
          <FilterButtons
            categories={categories}
            activeCategory={activeCategory}
            onCategoryChange={handleCategoryChange}
          />
        </FilterContainer>

        {loading ? (
          <LoadingMessage>Loading projects...</LoadingMessage>
        ) : error ? (
          <ErrorMessage>{error}</ErrorMessage>
        ) : filteredProjects.length === 0 ? (
          <NoProjectsMessage>No projects found matching your criteria.</NoProjectsMessage>
        ) : (
          <ProjectsGrid
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
          >
            {filteredProjects.map(project => (
              <ProjectCard key={project._id || project.id} project={project} />
            ))}
          </ProjectsGrid>
        )}
      </ProjectsContainer>
    </PageTransition>
  );
};

const ProjectsContainer = styled(motion.div)`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const FilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

const ProjectsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
`;

const LoadingMessage = styled.p`
  text-align: center;
  font-size: 1.2rem;
  margin: 3rem 0;
`;

const ErrorMessage = styled.p`
  text-align: center;
  color: var(--error-color);
  font-size: 1.2rem;
  margin: 3rem 0;
`;

const NoProjectsMessage = styled.p`
  text-align: center;
  font-size: 1.2rem;
  margin: 3rem 0;
`;

export default Projects;